import { forEach, uniqWith } from 'lodash';
import { IParamsResolver } from '../interfaces/params.resolver';
import { DotBasedValueResolver } from './dot.based.value.resolver';

import { ValueAdapterProvider } from './value.adapter.provider';

export class QueryParamsResolver implements IParamsResolver {
  uuid: string;
  delegate: IParamsResolver;
  ppFact: ValueAdapterProvider;
  values: Map<String, any> = new Map();
  valueReader: DotBasedValueResolver;

  // given delegate shall be used to 'get' values with prefix 'widget'
  constructor(adaptersFactory: ValueAdapterProvider, uuid: string, delegate: IParamsResolver) {
    this.ppFact = adaptersFactory;
    this.uuid = uuid;
    this.delegate = delegate;
    this.valueReader = new DotBasedValueResolver(delegate);
  }

  resolve(paramName: string): any {
    if (paramName === 'uuid') {
      return this.uuid;
    }
    return this.values.get(paramName);
  }

  getUUID(): string {
    return this.uuid;
  }

  resolveParams(queryParamsDef, inputs, widgetConfig): string[] {
    const paramNames: string[] = [];
    forEach(queryParamsDef, paramDef => {
      paramNames.push(paramDef.id);
      let pValue = this.valueReader.resolveParamValue(paramDef.value, inputs, widgetConfig);

      // convert Dates to timestamps
      if (pValue instanceof Date) {
        pValue = pValue.getTime();
      }

      if (paramDef.id === 'filter') {
        // OLPTC-6043 -if widget has configured to support only
        // certain filter keywords then lets filter out ones not supported
        if (widgetConfig && widgetConfig.options && widgetConfig.options.supportedFilterKeywords) {
          pValue = pValue.filter(exp =>
            widgetConfig.options.supportedFilterKeywords.includes(exp.keyword)
          );
          if (pValue.length && pValue[0].logicalOp) delete pValue[0].logicalOp;
        }

        // OLPTC-6102 - remove any single matching query definitions to keep redundancy out of filters
        pValue = uniqWith(pValue, function (val1, val2) {
          return (
            val1.keyword === val2.keyword &&
            val1.operator === val2.operator &&
            val1.operands &&
            val1.operands.length === 1 &&
            val2.operands &&
            val2.operands.length === 1 &&
            val1.operands[0] === val2.operands[0]
          );
        });
      }

      // is it an array?
      if (pValue && pValue.length === 0 && paramDef.default_value) {
        pValue = paramDef.default_value;
      } else if (!pValue && paramDef.default_value) {
        pValue = paramDef.default_value;
      }

      // note: even if you have defaulted to 'default_value', it is still passed via value postprocessor
      if (paramDef.value_adapter) {
        const modifiedValue =
          paramDef.id === 'filter' && paramDef.format === 'json'
            ? pValue
            : this.processValueWithPostprocessor(paramDef.value_adapter, pValue);
        this.values.set(paramDef.id, modifiedValue);
      } else {
        this.values.set(paramDef.id, pValue);
      }
    });
    // add 'uuid' manually - as we always need that param/it is not something that user can provide
    paramNames.push('uuid');
    return paramNames;
  }

  processValueWithPostprocessor(processorConfig, inValue) {
    const processor = this.ppFact.getPostprocessor(processorConfig);
    return processor.processValue(inValue);
  }
}
